import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Icon, type IconName } from '../primitives/Icon';

export interface StateAction {
  label: string;
  onClick: () => void;
  /** Present-but-disabled actions must say why. */
  disabledReason?: string | null;
  variant?: 'primary' | 'secondary' | 'ghost';
}

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  /** Why there is nothing here — not merely that there is nothing here. */
  reason: string;
  icon?: IconName;
  actions?: React.ReactNode;
  size?: 'sm' | 'md';
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { title, reason, icon = 'search', actions, size = 'md', className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center gap-2 text-center',
        size === 'md' ? 'px-6 py-12' : 'px-4 py-6',
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-surface-sunken text-content-muted"
      >
        <Icon name={icon} size={18} />
      </span>
      <h3 className="text-body-lg font-semibold text-content">{title}</h3>
      <p className="max-w-md text-dense-lg text-content-secondary">{reason}</p>
      {children}
      {actions ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
});
